import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const FormCard = styled.form`
  border: 1px solid #dbeafe;
  border-radius: 1rem;
  background: #f8fbff;
  padding: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;
const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.82rem;
  color: #475569;
  font-weight: 600;
`;
const FieldHint = styled.span`
  font-size: 0.76rem;
  color: #475569;
  font-weight: 600;
`;

const Input = styled.input`
  border: 1px solid #cbd5e1;
  border-radius: 0.6rem;
  min-height: 2.5rem;
  padding: 0.55rem 0.7rem;
  background: #fff;
  color: #0f172a;
`;

const Select = styled.select`
  border: 1px solid #cbd5e1;
  border-radius: 0.6rem;
  min-height: 2.5rem;
  padding: 0 0.65rem;
  background: #fff;
  color: #0f172a;
`;

const SubmitWrap = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SubmitButton = styled.button`
  border: none;
  border-radius: 0.7rem;
  background: #2563eb;
  color: #fff;
  font-weight: 700;
  font-size: 0.92rem;
  padding: 0.65rem 1.1rem;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.75 : 1)};
`;

const StatusText = styled.p`
  margin: 0.7rem 0 0;
  color: ${({ $error }) => ($error ? '#b91c1c' : '#1d4ed8')};
  font-size: 0.9rem;
`;

const TodayCard = styled.div`
  margin-top: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #fff;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 0.75rem 0.9rem;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  font-weight: 700;
  font-size: 0.9rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  gap: 0.5rem;
  padding: 0.75rem 0.9rem;
  border-top: 1px solid #f8fafc;
  color: #0f172a;
  font-size: 0.9rem;
`;

const Empty = styled.div`
  padding: 1rem;
  color: #64748b;
  text-align: center;
`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.login_id ? parsed : null;
  } catch {
    return null;
  }
};

const formatPracticeType = (value) => {
  if (value === 'position') return '자리연습';
  if (value === 'sentence') return '단문연습';
  return '낱말연습';
};

const formatStatus = (status) => {
  if (status === 'approved') return '승인 완료';
  if (status === 'rejected') return '반려';
  return '승인 대기';
};

const parseDurationToSeconds = (raw) => {
  const value = String(raw || '').trim();
  if (!value) return 0;
  if (value.includes(':')) {
    const parts = value.split(':').map((part) => part.trim());
    if (parts.length < 2) return NaN;
    // 1:56:99 처럼 추가 토큰이 있어도 앞의 "분:초"만 사용한다.
    const min = Number(parts[0]);
    const sec = Number(parts[1]);
    if (!Number.isFinite(min) || !Number.isFinite(sec)) return NaN;
    return min * 60 + sec;
  }
  return Number(value);
};

const formatDurationGuide = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '-';
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return `${min}분 ${sec}초 (${seconds}초)`;
};

const isValidAccuracy = (value) => {
  const text = String(value || '').trim();
  if (!text) return false;
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return false;
  const num = Number(text);
  return Number.isFinite(num) && num >= 0 && num <= 100;
};

const PortalTypingTodayPage = () => {
  const { request, loading } = useApi();
  const portalUser = useMemo(() => readPortalUser(), []);
  const [requests, setRequests] = useState([]);
  const [message, setMessage] = useState('');
  const [durationHint, setDurationHint] = useState('-');
  const [form, setForm] = useState({
    practice_type: 'word',
    typing_speed: '',
    best_typing_speed: '',
    accuracy: '',
    duration_input: ''
  });

  const fetchRequests = async () => {
    if (!portalUser?.login_id) return;
    try {
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/typing/student/today-requests?login_id=${encodeURIComponent(portalUser.login_id)}`
      });
      setRequests(data?.requests || []);
    } catch (error) {
      setMessage(error?.responseData?.message || '승인 요청 내역을 불러오지 못했습니다.');
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalUser?.login_id]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const submitTodayRequest = async () => {
    if (!portalUser?.login_id) return;
    setMessage('');
    const durationSeconds = parseDurationToSeconds(form.duration_input);
    if (!Number.isFinite(durationSeconds) || durationSeconds < 0) {
      setMessage('소요시간은 "1:36" 또는 "96" 형식으로 입력해 주세요.');
      return;
    }
    if (!isValidAccuracy(form.accuracy)) {
      setMessage('정확도는 0~100 사이 숫자로, 소수점 둘째 자리까지 입력해 주세요.');
      return;
    }
    try {
      await request({
        endpoint: `${BACKEND_BASE_URL}/api/typing/student/today-request`,
        method: 'POST',
        body: {
          login_id: portalUser.login_id,
          practice_type: form.practice_type,
          typing_speed: form.practice_type === 'position' ? null : Number(form.typing_speed || 0),
          best_typing_speed:
            form.practice_type === 'position' ? null : Number(form.best_typing_speed || form.typing_speed || 0),
          accuracy: Number(form.accuracy),
          duration_seconds: durationSeconds
        }
      });
      setForm((prev) => ({
        ...prev,
        typing_speed: '',
        best_typing_speed: '',
        accuracy: '',
        duration_input: ''
      }));
      setDurationHint('-');
      setMessage('승인 요청이 접수되었습니다. 선생님 승인 후 기록에 반영됩니다.');
      await fetchRequests();
    } catch (error) {
      setMessage(error?.responseData?.message || '기록 저장에 실패했습니다.');
    }
  };

  const handleRequestSubmitClick = async () => {
    if (!window.confirm('승인 요청하시겠습니까?')) {
      return;
    }
    await submitTodayRequest();
  };

  return (
    <Wrapper>
      <SectionHeading
        eyebrow="TODAY TYPING"
        title="오늘의 타자 기록"
        description="오늘 연습한 결과를 선생님께 한 번 보내 보세요. 승인되면 내 타자 기록에 남아요."
      />
      <FormCard
        onSubmit={(event) => event.preventDefault()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
          }
        }}
      >
        <FormRow>
          <Field>
            연습 유형
            <Select value={form.practice_type} onChange={handleChange('practice_type')}>
              <option value="word">낱말연습</option>
              <option value="sentence">단문연습</option>
              <option value="position">자리연습</option>
            </Select>
          </Field>
        </FormRow>
        <FormRow>
          {form.practice_type !== 'position' && (
            <>
              <Field>
                타수
                <Input
                  type="number"
                  min="0"
                  value={form.typing_speed}
                  onChange={handleChange('typing_speed')}
                  placeholder="예: 96타 -> 96"
                />
              </Field>
              <Field>
                최고 타수
                <Input
                  type="number"
                  min="0"
                  value={form.best_typing_speed}
                  onChange={handleChange('best_typing_speed')}
                  placeholder="예: 120타 -> 120"
                />
              </Field>
            </>
          )}
          {form.practice_type === 'position' && (
            <Field>
              자리연습 안내
              <Input value="자리연습은 타수/최고타수를 입력하지 않습니다." readOnly />
            </Field>
          )}
        </FormRow>
        <FormRow>
          <Field>
            정확도(%)
            <Input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.accuracy}
              onChange={handleChange('accuracy')}
              placeholder="예: 99.99"
            />
          </Field>
          <Field>
            소요 시간(초)
            <Input
              value={form.duration_input}
              onChange={(event) => {
                handleChange('duration_input')(event);
                const parsed = parseDurationToSeconds(event.target.value);
                setDurationHint(formatDurationGuide(parsed));
              }}
              placeholder="예: 1:36 또는 96"
            />
            <FieldHint>자동 변환: {durationHint}</FieldHint>
          </Field>
        </FormRow>
        <SubmitWrap>
          <SubmitButton type="button" disabled={loading} onClick={handleRequestSubmitClick}>
            {loading ? '요청 중...' : '승인 요청 보내기'}
          </SubmitButton>
        </SubmitWrap>
      </FormCard>
      <StatusText $error={/실패|오류|못|required|numeric/.test(message)}>{message}</StatusText>

      <TodayCard>
        <Header>타자기록 승인 요청 현황 ({requests.length}건)</Header>
        {requests.length > 0 ? (
          requests.map((record) => (
            <Row key={record.id}>
              <span>{record.request_date || '-'}</span>
              <span>{formatPracticeType(record.practice_type)}</span>
              <span>{record.typing_speed || '-'} 타/분</span>
              <span>{record.accuracy || 0}%</span>
              <span>{formatStatus(record.status)}</span>
            </Row>
          ))
        ) : (
          <Empty>{loading ? '불러오는 중...' : '승인 요청 내역이 아직 없습니다.'}</Empty>
        )}
      </TodayCard>
    </Wrapper>
  );
};

export default PortalTypingTodayPage;
